import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import api from "../../../services/api";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import toastOptions from "../../../utils/toastOptions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faPrint } from "@fortawesome/free-solid-svg-icons";
import states from "../../../assets/br.citys.json";
import { utils } from "../../../utils/utils";
import DialogConfirm from "../../../components/DialogConfirm";
import Modal from "../../../components/modal";
import { TypeHistory } from "../../../models/enums/TypeHistory";

interface Params {
  id: number;
  name: string;
  open: boolean;
  afterHandleSubmit: any;
  afterCloseModal: any;
  type?: TypeHistory;
}

enum SignTypePerson {
  OTHER = "OUTRO"
}

export default function MemberLetter(props: Params) {
  const {
    id,
    name,
    open,
    afterHandleSubmit,
    afterCloseModal,
  } = props;

  const [modalIsOpen, setModelOpen] = useState<boolean>(false);
  const [cities, setCities] = useState<string[]>([]);
  const [isOtherPerson, setOtherPerson] = useState<boolean>(false);
  const [openConfirmation, setConfirmationOpen] = useState<boolean>(false);
  const [letter, setLetter] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRecommendationLetter, setIsRecommendationLetter] = useState<boolean>(false);
  const [isChangeLetter, setIsChangeLetter] = useState<boolean>(false);
  const [reason, setReason] = useState<TypeHistory>();

  const schema = yup.object().shape({
    state: yup.string(),
    city: yup.string().when('state', {
      is: (value) => value != null && value != '',
      then: yup.string().required("Estado informado, Cidade obrigatória"),
    }),
    signPerson: yup.string()
      .when([], {
        is: () => isRecommendationLetter,
        then: (schema: any) => schema.required('Quem irá assinar é obrigatório'),
        otherwise: (schema: any) => schema.notRequired()
    }),
    otherSignPerson: yup.string().when('signPerson', {
      is: isRecommendationLetter && SignTypePerson.OTHER,
      then: yup.string().required("Quem irá assinar é obrigatório")
    }),
    note: yup.string(),
    reason: yup.string()
      .when([], {
        is: () => !isRecommendationLetter,
        then: (schema: any) => schema.required('Motivo da Carta é obrigatório'),
        otherwise: (schema: any) => schema.notRequired()
    })
  });

  const { register, handleSubmit, reset, clearErrors, errors, formState } =
    useForm({
      resolver: yupResolver(schema),
    });

  useEffect(() => {
    setModelOpen(open);
    setIsRecommendationLetter(props.type === TypeHistory.RECOMMENDATION_LETTER);
    setIsChangeLetter(props.type === TypeHistory.CHANGE_LETTER);
  }, [id, open]);

  const closeModal = () => {
    handleCloseModal();
    afterCloseModal();
  };

  const handleCloseModal = () => {
    setModelOpen(false);
    afterCloseModal();
  };

  const selectCities = (state: string) => {
    setCities([]);
    const citiesFromState = states
      .filter((s) => s.initial === state)
      .shift()?.cities;
    if (citiesFromState) {
      setCities(citiesFromState);
    }
  };

  const handleSignPerson = (signPersonSelected: string) => {
    setOtherPerson(signPersonSelected == SignTypePerson.OTHER);
    clearErrors();
  }

  const onSubmit = async (request: any) => {
    try {
      setIsLoading(true);  
      request.idMember = id;
      request.type = props.type;

      if (!isRecommendationLetter) {
        request.signPerson = utils.standardLetterSignPerson();
      }

      setLetter(request);

      const { data } = await api.post(`letters/member/${id}`, request);
    
      const nameLetter = `${name.toLocaleLowerCase().split(' ')[0]}-carta-${isRecommendationLetter ? 'recomendação' : 'mudança'}-${new Date().getTime()}.pdf`;

      utils.printPDF(nameLetter, data.item.report);

      setLetter((prevLetter: any) => ({
        ...prevLetter,
        token: data.item.token
      }));

      setConfirmationOpen(true);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessLetter = async (confirmationCode: string) => {
    try {
      if (!isRecommendationLetter) {
        if (!confirmationCode) {
          toast.warning("Informe a senha", toastOptions);
          return;
        }

        letter.confirmationCode = confirmationCode;
      }

      setIsLoading(true);
      dialogClose();
      await api.post(`letters/member/${id}/response`, letter);
      toast.success(`Carta de ${isRecommendationLetter ? 'Recomendação': 'Mudança'} impressa!`, toastOptions);
      handleCloseModal();
      afterHandleSubmit(letter.type);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIsLoading(false);
    }
  }

  const dialogClose = () => {
    setConfirmationOpen(false);
  }

  if (!props.type) return <></>;

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        className="modal-small"
        shouldCloseOnOverlayClick={false}
      >
        <div className="uk-modal-header">
          <h2 className="uk-modal-title">
            Carta de {isRecommendationLetter ? 'Recomendação' : 'Mudança'}
          </h2>
        </div>
        <button
          className="uk-modal-close-default"
          uk-close="true"
          type="button"
          onClick={() => handleCloseModal()}
        />
        <form onSubmit={handleSubmit<any>(onSubmit)} onReset={reset}>
          <div className="uk-modal-body">
            <div className="uk-grid-small" uk-grid="true">
              <div className="uk-width-1@s">
                Emitir Carta de {isRecommendationLetter ? 'Recomendação' : 'Mudança'}{": "}
                <label className="uk-text-bold">{name}</label>
              </div>
              {isChangeLetter && <>
                <span className="uk-text-small uk-text-light uk-text-danger uk-width-1@s uk-margin-small-bottom">
                  <FontAwesomeIcon icon={faCircleExclamation} className="uk-margin-small-right" /> 
                  Atenção!<br/>
                  <ul className="uk-margin-remove-top">
                    <li>Nesta tela irá fazer a impressão e ao [Confirmar]:</li>
                    <li>O pastor presidente e o secretário geral serão notificados, via e-mail, para assinarem a carta fisicamente</li>
                    <li>O membro ficará <b>ATIVO</b> até que faça a [Confirmação] na tela de Secretaria/Cartas Emitidas</li>
                    <li>O processo pode levar até 7 dias, devido a agenda do pastor e do secretário geral</li>
                  </ul>
                </span>
                <div className="uk-width-1-3@s">
                  <label className="uk-form-label" htmlFor="reason">Motivo da Carta</label>
                  <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                    <select className="uk-form-small"
                      id="reason" 
                      name="reason"
                      defaultValue={""}
                      ref={register}
                      onChange={(el) => setReason(el.target.value as TypeHistory)}
                    >
                      <option value="" key={`reason0`}>Selecione...</option>
                      <option value={TypeHistory.CHANGE_CHURCH} key={`reason1`}>
                        {TypeHistory.CHANGE_CHURCH}
                      </option>
                      <option value={TypeHistory.CHANGE_CITY} key={`reason2`}>
                        {TypeHistory.CHANGE_CITY}
                      </option>
                      <option value={TypeHistory.OTHER} key={`reason3`}>
                        {TypeHistory.OTHER}
                      </option>
                    </select>
                    <button 
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{textTransform: 'none'}} 
                      type="button" 
                      tabIndex={-1}
                    >
                      <span></span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                  <small className="uk-text-danger">{errors.reason?.message}</small>
                </div>
              </>}
              {isRecommendationLetter && <div className="uk-width-1@s">
                <div className="uk-form-controls uk-flex uk-flex-column">
                  <span className="uk-text-small uk-text-light uk-text-italic uk-margin-small-bottom">
                    A assinatura do <span className="uk-text-bold">Pr. Tiago Fogaça</span> constará na carta digital                      
                  </span>
                  <label className="uk-form-label" htmlFor="signPerson">Quem vai assinar como emissor?</label>
                  <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                    <select className="uk-form-small"
                        id="signPerson" 
                        name="signPerson"
                        defaultValue={""}
                        ref={register}
                        onChange={(el) => handleSignPerson(el.target.value)}
                    >
                        <option value="" key={`signPerson0`}>Selecione...</option>
                        <option value="Pr. Samuel José Alves / 1º Secretário / CGADB 95.546" key={`signPerson1`}>
                          {utils.standardLetterSignPerson()}
                        </option>
                        <option value="Pr. Frederico Martins Alves da Silva / 2º Secretário / CGADB 109.379" key={`signPerson2`}>
                          Pr. Frederico Martins Alves da Silva / 2º Secretário / CGADB 109.379
                        </option>
                        <option value="Coop. Guilherme A. de Oliveira Boccia / Secretário Adjunto" key={`signPerson3`}>
                          Coop. Guilherme Augusto de Oliveira Boccia / Secretário Adjunto
                        </option>                          
                        <option value={SignTypePerson.OTHER} key={`signPerson4`}>Outro</option>
                    </select>
                    <button 
                        className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                        style={{textTransform: 'none'}} 
                        type="button" 
                        tabIndex={-1}
                    >
                        <span></span>
                        <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                </div>
                <small className="uk-text-danger">{errors.signPerson?.message}</small>
              </div>}
              {
                isOtherPerson && (
                  <div className="uk-width-1@s">
                    <div className="uk-form-controls">
                      <label className="uk-form-label" htmlFor="otherSignPerson">
                        Especifique o nome e a função separado por /. Ex: Pr. Beltrano / Dirigente <br />
                        <FontAwesomeIcon icon={faCircleExclamation} className="uk-margin-small-right uk-text-primary" />
                        Neste caso, será necessário imprimir e assinar fisicamente a carta
                      </label>
                      <div className="uk-form-controls">
                        <input type="text" 
                          id="otherSignPerson" 
                          name="otherSignPerson" 
                          className="uk-input uk-form-small"
                          autoComplete={"off"}
                          ref={register}
                        />
                      </div>
                    </div>
                    <small className="uk-text-danger">{errors.otherSignPerson?.message}</small>
                  </div>      
                )
              }
              {(isRecommendationLetter || reason === TypeHistory.CHANGE_CITY) && <>                
                <div className="uk-width-1-3@s">
                  <label className="uk-form-label" htmlFor="state">
                    Estado destino
                  </label>
                  <div
                    uk-form-custom="target: > * > span:first-child"
                    className="uk-width-1"
                  >
                    <select
                      className="uk-form-small"
                      id="state"
                      name="state"
                      ref={register}
                      onChange={(el) => selectCities(el.target.value)}
                    >
                      <option value="" key="state0">
                        Selecione...
                      </option>
                      {states.map((f) => {
                        return (
                          <option value={f.initial} key={`state${f.initial}`}>
                            {f.name}
                          </option>
                        );
                      })}
                    </select>
                    <button
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{ textTransform: "none" }}
                      type="button"
                      tabIndex={-1}
                    >
                      <span></span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                  <small className="uk-text-danger">
                    {errors.state?.message}
                  </small>
                </div>
                <div className="uk-width-1-3@s">
                  <label className="uk-form-label" htmlFor="city">
                    Cidade destino
                  </label>
                  <div
                    uk-form-custom="target: > * > span:first-child"
                    className="uk-width-1"
                  >
                    <select
                      className="uk-form-small"
                      id="city"
                      name="city"
                      ref={register}
                      disabled={cities.length === 0}
                    >
                      <option value="" key={`city0`}>
                        Selecione...
                      </option>
                      {cities.map((c) => {
                        return (<option value={c} key={c}>{c}</option>);
                      })}
                    </select>
                    <button
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{ textTransform: "none" }}
                      type="button"
                      tabIndex={-1}
                      disabled={cities.length === 0}
                    >
                      <span></span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                  <small className="uk-text-danger">
                    {errors.city?.message}
                  </small>
                </div>              
              </>}
              <div className="uk-width-1@s">
                <label className="uk-form-label" htmlFor="note">
                  Observações
                </label>                
                <textarea
                  id="note"
                  name="note"
                  className="uk-textarea uk-form-small"
                  rows={4}
                  style={{ resize: "none" }}
                  defaultValue={""}
                  autoComplete={"off"}
                  ref={register}
                />
                <label className="uk-text-small uk-text-light uk-text-italic">
                  Ao inserir algum valor aqui, ele virá imediamente após os dizeres iniciais: <br/> 
                  Observamos que [sua observação]
                </label>
                <small className="uk-text-danger">{errors.note?.message}</small>
              </div>
            </div>
          </div>
          <div className="uk-modal-footer">
            <div
              className="uk-grid-small uk-child-width-1-4@s uk-flex-center uk-text-center"
              uk-grid="true"
            >
              <button
                className="uk-button uk-button-default uk-button-small uk-modal-close uk-margin-left"
                onClick={() => handleCloseModal()}
                disabled={formState.isSubmitting}
                type="button"
              >
                Cancelar
              </button>
              <button
                disabled={(formState.isSubmitting || isLoading)}
                className="uk-button uk-button-primary uk-button-small uk-margin-small-left"
                type="submit"
              >
                {(formState.isSubmitting || isLoading) && <div uk-spinner="true; ratio:0.5"></div>}
                Imprimir
                <FontAwesomeIcon className="uk-margin-small-left" icon={faPrint} />
              </button>
            </div>
          </div>
        </form>
      </Modal>
      <DialogConfirm
        open={openConfirmation}
        text={[
          `A Carta de ${isRecommendationLetter ? 'Recomendação' : 'Mudança'} para ${name} foi impressa corretamente?`,
          `Se sim clique em [Confirmar], se não clique em [Cancelar] e tente novamente.`
        ]}
        title={`Carta de ${isRecommendationLetter ? 'Recomendação' : 'Mudança'} Impressa?`}
        handleConfirmation={handleProcessLetter}
        afterClose={dialogClose}
        confirmTextLabel={isChangeLetter ? "Informe sua senha para confirmar" : undefined}
      ></DialogConfirm>
    </>
  );
}